import React, { useMemo } from 'react'
import { useChartExport } from '../../hooks/useChartExport'
import type { FfpDashboardData, PeriodKey } from '../../types'
import { bandRanges, formatPercent, longMonth, peopleHave, sincePhrase, sliceSeries } from '../../utils'
import { Card } from '../Card'
import { BandsChart } from '../charts/BandsChart'
import { Reveal } from '../Reveal'
import { BAND_NAMES, cardFooter, RETIREMENT } from '../../copy'
import { Meaning, Metric, Note } from '../CardParts'
import { ChangePill, Figure, NoFigures } from './parts'

type Props = {
  data: FfpDashboardData
  period: PeriodKey
  highlighted: boolean
  onAbout: () => void
}

type BandMonth = FfpDashboardData['health']['retirement_bands']['series'][number]
type ShownMonth = BandMonth & { shares: number[]; counts: number[] }

const isShown = (month: BandMonth): month is ShownMonth => month.shares !== null && month.counts !== null

// "Likely" plus "Highly likely", the top two bands, as a whole percent from the
// counts, the way the overview rounds it.
const likelyShare = (month: ShownMonth): number =>
  Math.round((month.counts.slice(-2).reduce((sum, count) => sum + count, 0) * 100) / month.people)

// "Up from 24% in Feb 2026", against the first month in the period.
const changeSince = (first: ShownMonth, latest: ShownMonth): string | null => {
  if (first.month === latest.month) return null

  const from = likelyShare(first)
  const now = likelyShare(latest)
  if (from === now) return `No change since ${longMonth(first.month)}`
  return `${now > from ? 'Up' : 'Down'} from ${formatPercent(from)} in ${longMonth(first.month)}`
}

// Band moves count from each person's first month to their latest, whatever
// the period. None while progress or the moves are withheld (fewer than 5 people).
const movesNote = (data: FfpDashboardData): string | null => {
  const { moved_up_band: up, moved_down_band: down } = data.progress.changes
  if (data.progress.people_counted === 0 || up === null || down === null) return null

  const since = sincePhrase(data.first_month)
  return `${up} ${peopleHave(up)} moved up at least one band ${since}. ${down} ${down === 1 ? 'has' : 'have'} moved down.`
}

export const RetirementCard = ({ data, period, highlighted, onAbout }: Props) => {
  const bands = data.health.retirement_bands
  // Months under 5 people have their shares withheld: they stay off the chart
  // (the panel's table lists them as hidden).
  const months = useMemo(() => sliceSeries(bands.series, period), [bands.series, period])
  const points = useMemo(() => months.filter(isShown), [months])
  const ranges = useMemo(() => bandRanges(bands.bands), [bands.bands])
  const { chartRef, exportImage } = useChartExport<'bar'>()
  const chip = `${bands.excluded} excluded · ${bands.pending} pending`
  const first = points[0]
  const latest = points[points.length - 1]
  // "On course" only when the likely share the card shows has gone up.
  const improving = first && latest && likelyShare(latest) > likelyShare(first)
  const title = improving ? RETIREMENT.title : `${RETIREMENT.name} ${sincePhrase(first ? first.month : data.first_month)}`

  const figure = latest ? (
    <Figure value={formatPercent(likelyShare(latest))} label="are likely or highly likely to retire comfortably">
      <ChangePill text={changeSince(first, latest)} />
    </Figure>
  ) : (
    <NoFigures />
  )
  const chart = latest ? <BandsChart ref={chartRef} series={points} names={BAND_NAMES} ranges={ranges} label="Share of people in each retirement readiness band, month by month" /> : null

  return (
    <Reveal>
      <Card
        question={RETIREMENT.question}
        title={title}
        onAbout={onAbout}
        highlighted={highlighted}
        exportImage={latest ? exportImage : undefined}
        exportName={RETIREMENT.name}
        footer={{ text: cardFooter(data, months, bands.people), chip }}
      >
        <Metric text={RETIREMENT.metric} />
        {figure}
        {chart}
        <Note text={movesNote(data)} />
        <Meaning text={RETIREMENT.meaning} />
      </Card>
    </Reveal>
  )
}
